import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import loggingService from './services/LoggingService';
import * as Sentry from '@sentry/react';

// --- Firebase Imports ---
import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

// --- SENTRY ERROR MONITORING ---
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5,
    sendDefaultPii: false,
  });
}

// Enable App Check debug token in development environment
if (process.env.NODE_ENV === 'development') {
  window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

// [SECURITY] Environment Hardening (Production Only)
if (process.env.NODE_ENV === 'production') {
  // 1. Disable Console Logs to prevent data leakage (keep console.error for Sentry)
  const noOp = () => { };
  console.log = noOp;
  console.info = noOp;
  console.warn = noOp;
  // Note: console.error is intentionally left enabled so Sentry can capture errors

  // 2. Disable Right Click (Deterrence)
  document.addEventListener('contextmenu', (e) => {
    e.preventDefault();
  });

  // 3. Disable specific keyboard shortcuts (F12, Ctrl+Shift+I, etc.)
  document.onkeydown = function (e) {
    if (e.keyCode === 123) { return false; } // F12
    if (e.ctrlKey && e.shiftKey && e.keyCode === 'I'.charCodeAt(0)) { return false; } // Ctrl+Shift+I
    if (e.ctrlKey && e.shiftKey && e.keyCode === 'C'.charCodeAt(0)) { return false; } // Ctrl+Shift+C
    if (e.ctrlKey && e.shiftKey && e.keyCode === 'J'.charCodeAt(0)) { return false; } // Ctrl+Shift-J
    if (e.ctrlKey && e.keyCode === 'U'.charCodeAt(0)) { return false; } // Ctrl-U
  }
}

// Firebase Configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase App
const app = initializeApp(firebaseConfig);

// Initialize Auth
export const auth = getAuth(app);

// Initialize Firestore
export const db = getFirestore(app);

// Initialize Storage
export const storage = getStorage(app);

// Initialize Google Auth Provider
export const googleProvider = new GoogleAuthProvider();

// Initialize App Check
try {
  const appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true
  });
  console.log("Firebase App Check initialized.");
} catch (error) {
  // console.error is disabled in production, but valid in dev
  if (process.env.NODE_ENV !== 'production') {
    console.error("Failed to initialize App Check:", error);
  }
}

// Initialize logging service
loggingService.initialize(db);

// [SECURITY] Removed global exposure of DB
// window.db = db;  <-- REMOVED

// Export the services so AuthContext and components can use them securely
// Export already done above, keep this comment for reference

const ErrorFallback = ({ eventId }) => (
  <div style={{
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', padding: '2rem',
    background: '#0F172A', color: '#F8FAFC', textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif',
  }}>
    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
    <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>Something went wrong</h2>
    <p style={{ color: '#94A3B8', fontSize: '0.875rem', maxWidth: '360px', marginBottom: '1.5rem' }}>
      An unexpected error occurred. Our team has been notified automatically.
    </p>
    {eventId && (
      <p style={{ color: '#64748B', fontSize: '0.75rem', marginBottom: '1.5rem', fontFamily: 'monospace' }}>
        Event ID: {eventId}
      </p>
    )}
    <button
      onClick={() => window.location.reload()}
      style={{
        background: '#F59E0B', color: '#0F172A', border: 'none',
        padding: '8px 20px', borderRadius: '6px', fontWeight: 600,
        fontSize: '0.875rem', cursor: 'pointer',
      }}
    >
      Reload Page
    </button>
  </div>
);

const root = createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={({ eventId }) => <ErrorFallback eventId={eventId} />} showDialog={false}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);